import { ArrowDownOutlined, ArrowUpOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import type { BrowserScript, ScriptActionNameMap } from "@src/utils/agent";
import { Button, Divider, Flex, Form as AntForm, Input, Select, Switch, type FormInstance } from "antd";
import React, { useCallback } from "react";
import { PayloadEditor } from "./PayloadEditor";
import styled from "styled-components";
import { usePermission } from "@src/frontend/hooks/usePermission";
import { LogtoPermission } from "@src/utils/constants";
import type { UserScriptModel } from "@src/generated/prisma/models";

export type FormData = Omit<UserScriptModel, 'id' | 'logto_uid'>;

const Form = styled(AntForm)`
  [data-hidden="true"] {
    display: none;
  }
`;

interface Props {
  form: FormInstance;
  onSubmit?: (data: FormData) => Promise<boolean>;
  isOwner?: boolean;
}

const scriptActionName: ScriptActionNameMap = {
  'GOTO': '跳转 URL',
  'INPUT': '输入文本',
  'CLICK': '点击元素',
  'SLEEP': '等待',
  'WAIT_ELEMENT': '等待元素出现',
  'RUN_JS': '运行脚本',
};

const options = Object.entries(scriptActionName)
  .map(([k, text]) => ({ label: text, value: k }));

export const ScriptEditForm = (props: Props) => {
  const { form, isOwner, onSubmit } = props;
  const watchActions = Form.useWatch<FormData>(value => value.actions ?? [], form) as BrowserScript[];
  const handleSubmit = useCallback(async () => {
    const data = await form.validateFields();
    console.debug('submit', data);
    const success = await onSubmit?.(data);
    if (!success) return;
    form.resetFields();
  }, [form, onSubmit]);
  const { hasPermission } = usePermission();
  return (
    <Form disabled={!isOwner} form={form} layout="vertical">
      <Form.Item label="脚本名称" name='name' rules={[{ required: true }]}>
        <Input />
      </Form.Item>
      <Form.Item label="描述" name="description" initialValue={''}>
        <Input.TextArea />
      </Form.Item>
      <Form.Item
        label='公开'
        name={'public'}
        data-hidden={!hasPermission(LogtoPermission.ACTION_PUBLISH)}
      >
        <Switch />
      </Form.Item>
      <Form.List name="actions">
        {(fields, opt) => (
          <Flex vertical gap={12}>
            {fields.map(({ name, key }, i) => {
              return (
                <React.Fragment key={key}>
                  <Flex vertical gap={8}>
                    <Button
                      icon={<PlusOutlined />}
                      onClick={() => opt.add({}, i)}
                      type="dashed"
                    >插入</Button>
                    <Divider>Step: {i + 1}</Divider>
                    <Flex gap={8}>
                      <Flex data-hidden={!isOwner} vertical gap={8} align="center" justify="center" flex={0}>
                        <Button disabled={i === 0} onClick={() => opt.move(i, i - 1)} style={{ height: '100%' }}><ArrowUpOutlined /></Button>
                        <Button disabled={i === fields.length - 1} onClick={() => opt.move(i, i + 1)} style={{ height: '100%' }}><ArrowDownOutlined /></Button>
                      </Flex>
                      <Flex vertical flex={1} gap={8} style={{ border: '1px solid rgba(255, 255, 255, 0.4)', padding: 8, borderRadius: 8 }}>
                        <Form.Item rules={[{ required: true }]} label="动作" name={[name, 'action']}>
                          <Select options={options} />
                        </Form.Item>
                        <PayloadEditor step={i} name={[name, 'payload']} action={watchActions[name]} />
                      </Flex>
                      <Flex data-hidden={!isOwner} flex={0}>
                        <Button block danger style={{ height: '100%' }} onClick={() => opt.remove(name)}><DeleteOutlined /></Button>
                      </Flex>
                    </Flex>
                  </Flex>
                </React.Fragment>
              );
            })}
            <Button data-hidden={!isOwner} type='dashed' icon={<PlusOutlined />} onClick={() => opt.add({})}>添加动作</Button>
            <Divider>共 {watchActions.length} 个动作</Divider>
            <Button disabled={false} block type="primary" onClick={handleSubmit}>
              {!isOwner ? '复制' : '提交'}
            </Button>
          </Flex>
        )}
      </Form.List>
    </Form>
  );
}